import React, { useRef, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, CircularProgress, Typography, Box } from "@mui/material";

// Placeholder for external helpers
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isNumericHeader = (header) => {
    // Check for and explicitly exclude descriptive keys (updated to include GROUP)
    if (header.toUpperCase().includes('ACCOUNT') || header.toUpperCase().includes('CUSTOMER') || header.toUpperCase().includes('SALESPERSON') || header.toUpperCase().includes('GROUP') || header.toUpperCase().includes('BRAND')) {
        return false;
    }
    
    // Check if it contains common numeric keywords
    return ['SALES', 'RETURN', 'VAT', 'COMMISSION', 'NET', '%', 'AMT'].some(s => header.toUpperCase().includes(s));
};

// Define the specific columns the user wants to total in the footer (Updated for new API structure)
const FOOTER_TOTAL_KEYS = [
    'NETCASH SALES', 
    'NETCREDIT SALES', 
    'NET SALERETURN', 
    'VAT AMT', 
    'NET SALES EXCLVAT'
];

const SalesTableGroupAggregated = ({ rows, loading, loadNextPage, hasMore }) => { 
    // Set descriptive column key based on the expected data structure (CHANGED to GROUP)
    const descriptiveColumnKey = 'GROUP';

    const tableContainerRef = useRef(null);
    const handleTableScroll = () => {

        const currentTarget = tableContainerRef.current;

        if (!currentTarget || loading || !hasMore || rows.length === 0) return;

        const { scrollTop, scrollHeight, clientHeight } = currentTarget;

        // Load the next page when reaching the bottom (with a 10px buffer)
        if (scrollHeight - scrollTop <= clientHeight + 10) {
            loadNextPage();
        }
    };

    // 1. Dynamically determine all display columns based on the first row (MOVED UNCONDITIONALLY BEFORE EARLY RETURN)
    const allKeys = rows.length > 0 ? Object.keys(rows[0]) : [];
    const displayKeys = allKeys.filter(key =>
        !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', descriptiveColumnKey].includes(key)
    );

    // 2. Calculate the Grand Totals internally ONLY for the FOOTER_TOTAL_KEYS
    const internalGrandTotals = useMemo(() => {
        if (rows.length === 0) return {};

        // Identify keys for totaling: must be explicitly requested in FOOTER_TOTAL_KEYS
        const keysToTotal = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
        
        const totals = {};

        for (const key of keysToTotal) {
            totals[key] = rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        }

        return totals;
    }, [rows, displayKeys]);
    
    // Keys for the columns that will display a total in the footer, preserving display order
    const totalValueKeys = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));

    // Determine the descriptive column header for the label cell (The first column)
    const descriptiveHeader = descriptiveColumnKey.replace(/([A-Z])/g, ' $1').trim().toUpperCase();

    // Determine total columns for colspan calculation
    // Total columns = 1 (descriptive column) + displayKeys.length
    const totalColumns = 1 + displayKeys.length;
    
    // --- EDITED LOGIC to calculate descriptiveSpan and ensure alignment ---
    let descriptiveSpan = totalColumns; // Default to full span if no totals
    
    if (totalValueKeys.length > 0) {
        // Find the index of the first column that will have a total (e.g., 'NETCASH SALES') in the displayKeys array.
        const firstTotalKey = totalValueKeys[0];
        const firstTotalIndex = displayKeys.indexOf(firstTotalKey);
        
        // The span covers: 1 (Sticky Descriptive Column - GROUP) + firstTotalIndex (number of display columns before the total column).
        if (firstTotalIndex !== -1) {
            descriptiveSpan = 1 + firstTotalIndex;
        }
    }
    // --------------------

    // Conditional early return moved below hook calls
    if (rows.length === 0 && !loading) return null;


    return (
        <TableContainer
            ref={tableContainerRef}
            onScroll={handleTableScroll}
            sx={{
                maxHeight: 'calc(100vh - 350px)',
                overflowY: 'auto', width: '100%'
            }}
        >
            <Table stickyHeader aria-label="aggregated account sales report">

                <TableHead>
                    <TableRow>
                        {/* 1. First Descriptive Column Header (Sticky) */}
                        <TableCell style={{ minWidth: 200, position: 'sticky', left: 0, zIndex: 10, backgroundColor: '#e0f7fa', borderRight: '1px solid #ccc' }}>
                            {descriptiveHeader}
                        </TableCell>

                        {/* 2. Dynamic Headers for the rest of the columns */}
                        {displayKeys.map(key => (
                            <TableCell
                                key={key}
                                align={isNumericHeader(key) ? "right" : "left"}
                                style={{ minWidth: 120, whiteSpace: 'nowrap' }}
                            >
                                {key.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={`agg-acc-row-${index}`} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                            {/* 1. First Descriptive Column Cell (Sticky) */}
                            <TableCell style={{ position: 'sticky', left: 0, zIndex: 9, backgroundColor: '#ffffff', borderRight: '1px solid #ccc' }}>
                                {row[descriptiveColumnKey]}
                            </TableCell>

                            {/* 2. Data Cells */}
                            {displayKeys.map(key => (
                                <TableCell key={key} align={isNumericHeader(key) ? "right" : "left"}>
                                    {isNumericHeader(key) ? formatNumber(row[key]) : row[key]}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}

                    
                    {/* Load More Indicator - Placed correctly inside TableBody */}
                    {hasMore && (
                        <TableRow>
                            <TableCell colSpan={totalColumns} align="center">
                                <Box sx={{ py: 2 }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="caption" display="block">
                                        Loading more data...
                                    </Typography>
                                </Box>
                            </TableCell>
                        </TableRow>
                    )}
                    

                    {!loading && rows.length === 0 && (<TableRow><TableCell colSpan={totalColumns} align="center"><Typography variant="subtitle1" color="textSecondary">No sales data found for the selected filters.</Typography></TableCell></TableRow>)}
                </TableBody>

                {/* Fixed Grand Totals Footer */}
                {totalValueKeys.length > 0 && (
                    <TableFooter>
                        <TableRow sx={{ backgroundColor: '#80cbc4', borderTop: '3px double #000' }}>
                            {/* 1. Descriptive Span for the GRAND TOTAL label */}
                            <TableCell colSpan={descriptiveSpan} align="right"
                                sx={{ fontWeight: 'bold', fontSize: '1.1em', padding: '8px 16px', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#80cbc4' }}>
                                GRAND TOTAL:
                            </TableCell>
                            
                            {/* 2. Data cells starting from the column where the totals begin */}
                            {/* We iterate over the remaining displayKeys to ensure every column is accounted for. */}
                            {displayKeys.slice(descriptiveSpan - 1).map(key => {
                                const isTotalKey = FOOTER_TOTAL_KEYS.includes(key);
                                return (
                                    <TableCell key={key} align="right"
                                        sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#80cbc4' }}>
                                        {/* Show total if key is in FOOTER_TOTAL_KEYS, otherwise show blank */}
                                        {isTotalKey ? formatNumber(internalGrandTotals[key]) : ''}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableFooter>
                )}
            </Table>

            {loading && (
                <div style={{ display: "flex", justifyContent: 'center', alignItems: "center", width: "100%", height: "100%", position: "absolute", zIndex: 100, backgroundColor: "#ffffff80", top: "0px", left: "0px" }}>
                    <CircularProgress size={48} />
                </div>
            )}
        </TableContainer>
    );
};

export default SalesTableGroupAggregated;